import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveStickerStyle, getBadgeStyle, C } from '../albumColors';
import { stickerCode, stickerLocalNumber } from '../albumData';

const lockBadgeStyle = {
  position: 'absolute',
  bottom: -5,
  left: -5,
  paddingLeft: 4,
  paddingRight: 5,
  height: 16,
  minWidth: 22,
  backgroundColor: C.reservedRing,
  color: 'white',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  fontWeight: 800,
  gap: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  border: '1.5px solid white',
  zIndex: 11,
  letterSpacing: 0.2,
};

const valueBadgeStyle = {
  position: 'absolute',
  top: -4,
  left: -4,
  paddingLeft: 3,
  paddingRight: 3,
  height: 14,
  minWidth: 14,
  backgroundColor: '#F59E0B',
  color: 'white',
  borderRadius: 7,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 9,
  fontWeight: 800,
  gap: 1,
  boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  zIndex: 11,
};

const incomingBadgeStyle = {
  position: 'absolute',
  bottom: -5,
  right: -5,
  paddingLeft: 4,
  paddingRight: 5,
  height: 16,
  minWidth: 16,
  backgroundColor: '#2563eb',
  color: 'white',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  fontWeight: 800,
  gap: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  border: '1.5px solid white',
  zIndex: 11,
};

const LONG_PRESS_MS = 450;

const StickerCell = React.memo(function StickerCell({ sticker, viewMode, selected, extras, hideRepeated, onClick, theme }) {
  const { t } = useTranslation();
  const { style, badgeValue } = resolveStickerStyle(sticker, viewMode, selected, extras, theme);
  const badge = getBadgeStyle(viewMode, theme);
  const lockedCount = extras?.lockedSet?.get?.(sticker.n) ?? 0;
  const incomingSources = extras?.incomingSet?.get?.(sticker.n) ?? null;
  const incomingCount = incomingSources?.length ?? 0;
  const valueRating = extras?.valuationSet?.get?.(sticker.n) ?? 1;
  const showLock = lockedCount > 0 && sticker.owned && viewMode !== 'TRADE_REVIEW' && viewMode !== 'TRADE_DETAIL' && viewMode !== 'OFFER_REVIEW';
  const showIncoming = incomingCount > 0 && !sticker.owned && viewMode === 'MY_ALBUM';
  const showValue = valueRating > 1 && sticker.owned && viewMode === 'MY_ALBUM';
  const onValuate = extras?.onValuate;
  const canValuate = sticker.owned && viewMode === 'MY_ALBUM' && typeof onValuate === 'function';

  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const startLongPress = () => {
    if (!canValuate) return;
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      onValuate(sticker.n);
    }, LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleClick = (e) => {
    if (longPressFired.current) {
      // Suppress the click that follows a long-press.
      longPressFired.current = false;
      e.preventDefault?.();
      return;
    }
    onClick?.(e);
  };

  const handleContextMenu = (e) => {
    if (!canValuate) return;
    e.preventDefault();
    onValuate(sticker.n);
  };

  // Tooltip combinado: código + estado + extras (reservada, valoración).
  // Hover sobre cualquier punto de la celda muestra el contexto completo.
  const tooltipParts = [stickerCode(sticker.n)];
  if (viewMode === 'MY_ALBUM') {
    if (!sticker.owned) {
      tooltipParts.push(t('Te falta'));
    } else if ((sticker.reps || 1) > 1) {
      const effective = Math.max(0, (sticker.reps || 1) - lockedCount);
      tooltipParts.push(effective > 1
        ? t('Tenés {{n}} (1 en uso, {{free}} libres)', { n: sticker.reps, free: effective - 1 })
        : t('Tenés {{n}}', { n: sticker.reps }));
    } else {
      tooltipParts.push(t('Tenés'));
    }
    if (lockedCount > 0) tooltipParts.push(t('Reservada x{{n}}', { n: lockedCount }));
    if (showIncoming) {
      const names = (incomingSources || []).map(s => s.fromUserName).filter(Boolean);
      const uniq = Array.from(new Set(names));
      if (uniq.length === 1) {
        tooltipParts.push(t('Te la va a dar {{name}}', { name: uniq[0] }));
      } else if (uniq.length > 1) {
        tooltipParts.push(t('Te la van a dar: {{names}}', { names: uniq.join(', ') }));
      } else {
        tooltipParts.push(t('La vas a recibir en un intercambio'));
      }
    }
    if (valueRating > 1) tooltipParts.push(t('Valor {{n}}/5', { n: valueRating }));
  } else if (viewMode === 'USER_ALBUM') {
    tooltipParts.push(sticker.owned ? t('La tiene') : t('Le falta'));
  }
  const tooltip = tooltipParts.join(' · ');

  return (
    <div
      data-sticker-n={sticker.n}
      style={style}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onTouchStart={startLongPress}
      onTouchEnd={cancelLongPress}
      onTouchMove={cancelLongPress}
      onTouchCancel={cancelLongPress}
      title={tooltip}
    >
      {stickerLocalNumber(sticker.n)}
      {badgeValue != null && !hideRepeated && <div style={badge}>{badgeValue}</div>}
      {showLock && (
        <div style={lockBadgeStyle} title={t('Reservada en un intercambio activo (x{{n}}). Tocá para sumar otra copia.', { n: lockedCount })} aria-label={t('Reservada x{{n}}', { n: lockedCount })}>
          <span className="material-symbols-outlined" style={{ fontSize: 11, lineHeight: 1, fontVariationSettings: "'FILL' 1" }}>lock</span>
          {lockedCount}
        </div>
      )}
      {showIncoming && (
        <div style={incomingBadgeStyle} aria-label={t('Vas a recibir x{{n}}', { n: incomingCount })}>
          <span className="material-symbols-outlined" style={{ fontSize: 11, lineHeight: 1, fontVariationSettings: "'FILL' 1" }}>call_received</span>
          {incomingCount > 1 && incomingCount}
        </div>
      )}
      {showValue && (
        <div style={valueBadgeStyle} title={t('Lo valoro {{n}}/5', { n: valueRating })}>
          <span className="material-symbols-outlined" style={{ fontSize: 9, lineHeight: 1, fontVariationSettings: "'FILL' 1" }}>star</span>
          {valueRating}
        </div>
      )}
    </div>
  );
});

export default StickerCell;
